import React, { useCallback, useState, useEffect, useRef } from "react";

import { createPortal } from "react-dom";

import { FiSearch, FiCalendar, FiDownload, FiEye, FiEyeOff, FiFileText, FiX, FiAlertCircle, FiClock, FiShield, FiPaperclip, FiRefreshCw, FiUnlock, FiLock, FiPlus } from "react-icons/fi";

import "./staffcss/medicalappointments.css";

import jsPDF from "jspdf";

import { createPDF, createAppointmentSlipPDF } from "../../utils/pdfExport";
import * as XLSX from 'xlsx';

import { auth, db } from "../../firebase";

import { getDoc, doc } from "firebase/firestore";

import { appointmentService } from "../../services/appointmentService";
import { SERVICE_FILTER_OPTIONS, matchesServiceFilter } from "../../utils/appointmentUtils";
import {
  getAppointmentDateTimeValue,
  getAppointmentSortIndicator,
  getAppointmentSortTitle,
  getAppointmentTextValue,
  getNextAppointmentSortConfig,
  sortAppointments
} from "../../utils/appointmentSorting";

import Banner from "./Banner";
import Portal from "../../components/Portal";
import { CreateAppointmentModal } from "../AdminPage";

const getAssistanceBadgeColor = (reason) => {
  const colors = {
    minor: '#dbeafe',
    senior: '#fef3c7',
    pwd: '#d1fae5',
    nrnw: '#fce7f3',
    other: '#e5e7eb'
  };
  return colors[reason?.toLowerCase()] || colors.other;
};

const getAssistanceBadgeTextColor = (reason) => {
  const colors = {
    minor: '#1e40af',
    senior: '#92400e',
    pwd: '#065f46',
    nrnw: '#9d174d',
    other: '#374151'
  };
  return colors[reason?.toLowerCase()] || colors.other;
};



const STATUS_FILTER_TABS = [

  { label: "All Statuses", value: "" },

  { label: "Completed", value: "COMPLETED" },

  { label: "Cancelled by User", value: "CANCELLED_BY_USER" },

  { label: "Cancelled by You", value: "CANCELLED_BY_PRO" }

];



const getStatusLabel = (status) => {

  const mapping = {

    'COMPLETED': 'Completed',

    'CANCELLED_BY_USER': 'Cancelled by User',

    'CANCELLED_BY_PRO': 'Cancelled by You',

  };

  return mapping[status] || status;

};



const getStatusBadgeClass = (status) => {

  return `ma-badge-${status}`;

};

const getAttachmentExpiry = (appointment) => {
  if (!appointment) return null;

  if (appointment.editDeadline) {
    const deadline = new Date(appointment.editDeadline);
    if (!Number.isNaN(deadline.getTime())) return deadline;
  }

  const completedAt = appointment.completedAt?.toDate
    ? appointment.completedAt.toDate()
    : appointment.completedAt
      ? new Date(appointment.completedAt)
      : null;
  if (!completedAt || Number.isNaN(completedAt.getTime())) return null;

  // Legacy completed appointments use the same seven-day fallback as the edit guard.
  return new Date(completedAt.getTime() + (7 * 24 * 60 * 60 * 1000));
};



// ---------------- Export Modal ----------------
function ExportModal({ isOpen, onClose, onExport, isExporting }) {
  const [selectedFormat, setSelectedFormat] = React.useState(null);

  React.useEffect(() => {
    const scrollContainer = document.querySelector('.mp-workspace');
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousContainerOverflow = scrollContainer?.style.overflow ?? '';

    document.body.style.overflow = 'hidden';
    if (scrollContainer) {
      scrollContainer.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      if (scrollContainer) {
        scrollContainer.style.overflow = previousContainerOverflow;
      }
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedFormat(null);
    }
  }, [isOpen]);

  const handleExport = (format) => {
    setSelectedFormat(format);
    onExport(format);
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay export-modal-overlay">
        <div className="modal-content export-modal-content">
          <div className="modal-header">
            <h3 className="modal-title-white">Export Report</h3>
            <button className="modal-close" onClick={onClose}>
              <FiX />
            </button>
          </div>

          <div className="export-modal-body">
            <p className="export-modal-subtitle">Select export format</p>

            <div className="export-format-list">
              <button
                className={`export-format-btn ${selectedFormat === 'pdf' ? 'selected' : ''}`}
                onClick={() => handleExport('pdf')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap pdf">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">PDF Report</span>
                  <span className="export-format-desc">Presentation-ready report</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'excel' ? 'selected' : ''}`}
                onClick={() => handleExport('excel')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap excel">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">Excel .xlsx</span>
                  <span className="export-format-desc">Structured analytics workbook</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'csv' ? 'selected' : ''}`}
                onClick={() => handleExport('csv')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap csv">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">CSV .csv</span>
                  <span className="export-format-desc">Raw structured data</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}



const AppointmentHistory = ({ appointments, staffProfile, refreshAppointments, sidebarCollapsed }) => {

  const [search, setSearch] = useState("");

  const [dateFrom, setDateFrom] = useState("");

  const [dateTo, setDateTo] = useState("");

  const [serviceFilter, setServiceFilter] = useState("");

  const [statusFilter, setStatusFilter] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

  const [sortConfig, setSortConfig] = useState(null);

  const itemsPerPage = 10;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [appointmentsWithAssistance, setAppointmentsWithAssistance] = useState([]);
  const followUpAppointmentsCache = useRef(new Map());
  const followUpParentCache = useRef(new Map());
  const [followUpRefreshVersion, setFollowUpRefreshVersion] = useState(0);



  const [viewing, setViewing] = useState(null);

  // Tooltip state
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0, type: 'default' });

  // Tooltip handlers
  const handleActionMouseEnter = (e, text, type = 'default') => {
    setTooltip({
      visible: true,
      text: text,
      x: e.clientX - 150,
      y: e.clientY,
      type: type
    });
  };

  const handleActionMouseMove = (e) => {
    if (tooltip.visible) {
      setTooltip(prev => ({
        ...prev,
        x: e.clientX - 150,
        y: e.clientY
      }));
    }
  };

  const handleActionMouseLeave = () => {
    setTooltip({ visible: false, text: '', x: 0, y: 0, type: 'default' });
  };

  const [bannerMessage, setBannerMessage] = useState("");

  const [bannerType, setBannerType] = useState("error");
  const [followUpAppointmentTarget, setFollowUpAppointmentTarget] = useState(null);
  const [followUpFeedback, setFollowUpFeedback] = useState({ message: "", type: "error" });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const setFollowUpNotification = useCallback(({ message, type }) => {
    setFollowUpFeedback({ message, type: type || "error" });
  }, []);



  // Attachments State

  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);

  const [clinicalNote, setClinicalNote] = useState("");

  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);

  const [isUploading, setIsUploading] = useState(false);

  const [noteError, setNoteError] = useState("");

  const [existingNotes, setExistingNotes] = useState([]);
  const [selectedNoteId, setSelectedNoteId] = useState("");
  const [showAttachmentEditor, setShowAttachmentEditor] = useState(false);

  const [showBlockchainModal, setShowBlockchainModal] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selectedExportFormat, setSelectedExportFormat] = useState(null);
  const [isExporting, setIsExporting] = useState(false);



  // Keep 'viewing' in sync with real-time appointments prop

  // eslint-disable-next-line react-hooks/exhaustive-deps

  React.useEffect(() => {

    if (viewing) {

      const updated = appointmentsWithAssistance.find(a => a.id === viewing.id);

      if (updated) {

        setViewing(updated);

      }

    }

  }, [appointmentsWithAssistance, viewing?.id]);



  // Fetch latest appointment data when attachments modal opens

  // eslint-disable-next-line react-hooks/exhaustive-deps

  React.useEffect(() => {

    const fetchLatestAppointmentData = async () => {

      if (showAttachmentsModal && viewing?.id) {

        try {

          const apptDoc = await getDoc(doc(db, "appointments", viewing.id));

          if (apptDoc.exists()) {

            const data = apptDoc.data();

            setExistingNotes(data.attachmentNotes || []);
            const latestNote = data.attachmentNotes?.[data.attachmentNotes.length - 1];
            setSelectedNoteId(latestNote?.id || "");
            setClinicalNote(latestNote?.text || "");
            setShowAttachmentEditor(false);
            setRemovedAttachmentIds([]);

            setViewing(prev => ({

              ...prev,

              attachments: data.attachments || [],

              attachmentNotes: data.attachmentNotes || []

            }));

          }

        } catch (err) {

          console.error("Error fetching latest appointment data:", err);

        }

      }

    };

    fetchLatestAppointmentData();

  }, [showAttachmentsModal, viewing?.id]);



  // Helper to check if appointment is editable (within 7 days of completion/update)
  // Checkpoint 3: Also check blockchain status for read-only enforcement

  const isAppointmentEditable = (appt) => {

    if (!appt) return false;

    // Checkpoint 3: Check blockchain status first - if read_only or verified, immediately not editable
    // verified status means the record has been registered on blockchain and is immutable

    if (appt.blockchain?.status === 'read_only' || appt.blockchain?.status === 'verified') {

      return false;

    }

    const expiry = getAttachmentExpiry(appt);
    return appt.status === 'COMPLETED' && expiry ? expiry > new Date() : false;

  };



  const handleAttachmentUpload = async () => {
    if (!isAppointmentEditable(viewing)) {
      setNoteError("The grace period has expired. This appointment is read-only.");
      return;
    }

    if (!clinicalNote.trim() && attachmentFiles.length === 0) {

      setNoteError("Please enter a note or select at least one file.");

      return;

    }

    if (clinicalNote.trim() && clinicalNote.trim().length < 10) {

      setNoteError("Notes must be at least 10 characters long.");

      return;

    }

    if (clinicalNote.length > 500) {

      setNoteError("Notes must not exceed 500 characters.");

      return;

    }

    setIsUploading(true);

    setNoteError("");

    try {

      const sanitizedNote = clinicalNote.trim().replace(/<[^>]*>?/gm, '');

      const uploadedBy = staffProfile?.fullName || staffProfile?.name || "Medical Professional";

      await appointmentService.updateAttachmentContent(
        viewing.id,
        attachmentFiles,
        sanitizedNote,
        uploadedBy,
        viewing.attachments || [],
        removedAttachmentIds,
        selectedNoteId || null
      );

      setBannerMessage("Attachments and findings saved successfully!");

      setBannerType("success");

      setClinicalNote("");

      setAttachmentFiles([]);
      setRemovedAttachmentIds([]);

      // Refresh data

      const apptDoc = await getDoc(doc(db, "appointments", viewing.id));

      if (apptDoc.exists()) {

        const data = apptDoc.data();

        setExistingNotes(data.attachmentNotes || []);

        setViewing(prev => ({

          ...prev,

          attachments: data.attachments || [],

          attachmentNotes: data.attachmentNotes || []

        }));

      }

      setTimeout(() => {

        setBannerMessage("");

      }, 3000);

    } catch (err) {

      console.error("Error uploading attachment:", err);

      setBannerMessage("Failed to upload. " + err.message);

      setBannerType("error");

    } finally {

      setIsUploading(false);

    }

  };



  const handleDownload = async (appt) => {
    createAppointmentSlipPDF(appt, {
      status: appt.status,
      proName: (appt.status === "COMPLETED" || appt.status === "ACCEPTED")
        ? (appt.proName || "Medical Professional")
        : "To Be Assigned",
    });
  };

  const exportColumns = {
    includeStatus: !statusFilter,
    includeService: !serviceFilter,
    serviceLabel: serviceFilter || ''
  };

  const getExportPatientDetails = async (items) => {
    const uids = [...new Set(items.map(item => item.uid).filter(Boolean))];
    const details = {};
    await Promise.all(uids.map(async uid => {
      const snapshot = await getDoc(doc(db, 'users', uid));
      const data = snapshot.exists() ? snapshot.data() : {};
      const location = [
        data.contactDetails?.barangay,
        data.contactDetails?.province,
        data.contactDetails?.region
      ].filter(Boolean).join(', ') || data.personalInformation?.province || 'N/A';
      details[uid] = {
        uic: data.uniqueIdentifierCode || data.accountSetup?.uniqueIdentifierCode || 'N/A',
        dob: data.personalInformation?.dob || 'N/A',
        location,
        assistanceRequired: data.assistance?.assistanceRequired || false,
        reasonForAssistance: data.assistance?.reasonForAssistance || []
      };
    }));
    return details;
  };

  const getExportHeaders = () => [
    ...(exportColumns.includeStatus ? ['Status'] : []),
    'Patient', 'UIC', 'DOB', 'Address', 'Reference #', 'Clinic',
    ...(exportColumns.includeService ? ['Service'] : []),
    'Date & Time'
  ];

  const getExportRow = (apt, patientDetails) => [
    ...(exportColumns.includeStatus ? [getStatusLabel(apt.status)] : []),
    apt.patientName || 'N/A',
    patientDetails[apt.uid]?.uic || 'N/A',
    patientDetails[apt.uid]?.dob || 'N/A',
    patientDetails[apt.uid]?.location || 'N/A',
    apt.reference || 'N/A',
    apt.clinicName || 'N/A',
    ...(exportColumns.includeService ? [apt.serviceName || 'N/A'] : []),
    `${apt.date || 'N/A'} ${apt.time || 'N/A'}`
  ];

  const getExportGroups = () => statusFilter
    ? [{ label: `Status: ${getStatusLabel(statusFilter)}`, items: filteredAppointments, isFilterLabel: true }]
    : [
        { items: filteredAppointments.filter(item => item.status === 'COMPLETED') },
        { items: filteredAppointments.filter(item => item.status === 'CANCELLED_BY_USER') },
        { items: filteredAppointments.filter(item => item.status === 'CANCELLED_BY_PRO') }
      ].filter(group => group.items.length > 0);

  const exportDateRangeLabel = dateFrom || dateTo
    ? `Date Range: ${dateFrom || 'Any'} - ${dateTo || 'Any'}`
    : null;

  const exportToPDF = async () => {
    const medicalProfessionalName = staffProfile?.fullName || staffProfile?.name || null;
    const pdf = createPDF({ title: "Appointment History Report", medicalProfessionalName });
    const patientDetails = await getExportPatientDetails(filteredAppointments);
    let startY = pdf.contentStartY;
    pdf.doc.setFontSize(10);
    pdf.doc.text(`Total Appointments: ${filteredAppointments.length}`, pdf.margin, startY);
    if (exportDateRangeLabel) {
      pdf.doc.text(exportDateRangeLabel, pdf.doc.internal.pageSize.getWidth() - pdf.margin, startY, { align: 'right' });
    }
    startY += 15;
    if (exportColumns.serviceLabel) {
      pdf.doc.text(`Service: ${exportColumns.serviceLabel}`, pdf.margin, startY);
      startY += 10;
    }
    getExportGroups().forEach(group => {
      if (group.label) {
        pdf.doc.setFontSize(10);
        pdf.doc.setFont('helvetica', 'normal');
        pdf.doc.text(group.label, pdf.margin, startY);
        startY += 8;
      }
      pdf.autoTable({
        head: [getExportHeaders()],
        body: group.items.map(apt => getExportRow(apt, patientDetails)),
        startY,
        margin: { top: pdf.contentStartY, bottom: pdf.footerHeight + 10, left: pdf.margin, right: pdf.margin }
        ,styles: { overflow: 'linebreak' },
        columnStyles: { [exportColumns.includeStatus ? 4 : 3]: { cellWidth: 42 } }
      });
      startY = pdf.doc.lastAutoTable.finalY + 20;
    });
    pdf.save(`appointment-history-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportToExcel = async () => {
    const workbook = XLSX.utils.book_new();
    const patientDetails = await getExportPatientDetails(filteredAppointments);
    const medicalProfessionalName = staffProfile?.fullName || staffProfile?.name || null;
    const summaryData = [
      ['APPOINTMENT HISTORY REPORT'],
      ['Generated', new Date().toLocaleString()],
      ['Status Filter', statusFilter || 'All Statuses'],
      ...(exportColumns.serviceLabel ? [['Service', exportColumns.serviceLabel]] : []),
      ...(medicalProfessionalName ? [['Medical Professional', medicalProfessionalName]] : []),
      [],
      ['APPOINTMENTS SUMMARY'],
      ['Total Appointments', filteredAppointments.length, ...(exportDateRangeLabel ? ['Date Range', exportDateRangeLabel] : [])]
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
    const contentData = [['APPOINTMENT DETAILS']];
    if (exportColumns.serviceLabel) contentData.push([`Service: ${exportColumns.serviceLabel}`]);
    getExportGroups().forEach((group, index) => {
      if (index > 0) contentData.push([]);
      if (group.label) contentData.push([group.label]);
      contentData.push(getExportHeaders());
      group.items.forEach(apt => contentData.push(getExportRow(apt, patientDetails)));
    });
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(contentData), 'Appointments');
    XLSX.writeFile(workbook, `appointment-history-report-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToCSV = async () => {
    const patientDetails = await getExportPatientDetails(filteredAppointments);
    const medicalProfessionalName = staffProfile?.fullName || staffProfile?.name || null;
    const csvData = [
      ['APPOINTMENT HISTORY REPORT'],
      ['Generated', new Date().toLocaleString()],
      ['Status Filter', statusFilter || 'All Statuses'],
      ...(exportColumns.serviceLabel ? [['Service', exportColumns.serviceLabel]] : []),
      ...(medicalProfessionalName ? [['Medical Professional', medicalProfessionalName]] : []),
      [],
      ['APPOINTMENTS SUMMARY'],
      ['Total Appointments', filteredAppointments.length, ...(exportDateRangeLabel ? ['Date Range', exportDateRangeLabel] : [])],
      [],
      ['APPOINTMENT DETAILS']
    ];
    if (exportColumns.serviceLabel) csvData.push([`Service: ${exportColumns.serviceLabel}`]);
    getExportGroups().forEach((group, index) => {
      if (index > 0) csvData.push([]);
      if (group.label) csvData.push([group.label]);
      csvData.push(getExportHeaders(), ...group.items.map(apt => getExportRow(apt, patientDetails)));
    });
    const csvContent = csvData.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `appointment-history-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleExport = async (format) => {
    try {
      setIsExporting(true);
      setSelectedExportFormat(format);
      
      if (format === 'pdf') {
        await exportToPDF();
      } else if (format === 'excel') {
        await exportToExcel();
      } else if (format === 'csv') {
        await exportToCSV();
      }
    } catch (error) {
      console.error('Export error:', error);
      setBannerMessage('Export failed');
      setBannerType('error');
    } finally {
      setIsExporting(false);
      setSelectedExportFormat(null);
      setIsExportModalOpen(false);
    }
  };



  const matchesSearch = (appointment) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return [appointment, ...(appointment.followUpAppointments || [])].some(item =>
      item.patientName?.toLowerCase().includes(searchLower) ||
      item.clinicName?.toLowerCase().includes(searchLower) ||
      item.serviceName?.toLowerCase().includes(searchLower) ||
      item.reference?.toLowerCase().includes(searchLower) ||
      getStatusLabel(item.status).toLowerCase().includes(searchLower)
    );
  };

  const matchesDateRange = (appointment) => {
    const inRange = (item) => {
      const appointmentDate = new Date(item.date);
      return (!dateFrom || appointmentDate >= new Date(dateFrom)) &&
        (!dateTo || appointmentDate <= new Date(dateTo));
    };
    return inRange(appointment) || (appointment.followUpAppointments || []).some(inRange);
  };

  const matchesService = (appointment) =>
    matchesServiceFilter(appointment, serviceFilter) ||
    (appointment.followUpAppointments || []).some(followUp =>
      matchesServiceFilter(followUp, serviceFilter)
    );

  const filteredAppointments = appointmentsWithAssistance.filter(appointment =>
    matchesSearch(appointment) &&
    matchesDateRange(appointment) &&
    matchesService(appointment) &&
    (!statusFilter || appointment.status === statusFilter ||
      appointment.followUpAppointments?.some(followUp => followUp.status === statusFilter))
  );



  const getStatusTabCount = (tabValue) => {

    return appointmentsWithAssistance.filter(appointment =>
      matchesSearch(appointment) &&
      matchesDateRange(appointment) &&
      matchesService(appointment) &&
      (!tabValue || appointment.status === tabValue ||
        appointment.followUpAppointments?.some(followUp => followUp.status === tabValue))
    ).length;
  };



  // Pagination logic

  // Set loading to false when appointments are loaded (even if empty)
  useEffect(() => {
    setLoading(false);
  }, [appointments]);

  // Fetch patient assistance data for appointments
  useEffect(() => {
    let isActive = true;
    const fetchHistoryData = async () => {
      if (!appointments || appointments.length === 0) {
        followUpAppointmentsCache.current.clear();
        followUpParentCache.current.clear();
        setAppointmentsWithAssistance([]);
        return;
      }

      try {
        const appointmentById = new Map(appointments.map(appointment => [appointment.id, appointment]));
        const sourceAppointmentIds = [...new Set([
          ...appointments
            .filter(appointment => appointment.isFollowUp !== true)
            .map(appointment => appointment.id),
          ...appointments
            .filter(appointment => appointment.isFollowUp === true && appointment.originalAppointmentId)
            .map(appointment => appointment.originalAppointmentId)
        ])];
        const sourceIdsToFetch = sourceAppointmentIds.filter(sourceId =>
          !followUpAppointmentsCache.current.has(sourceId)
        );
        if (sourceIdsToFetch.length > 0) {
          const fetchedFollowUps = await appointmentService.getFollowUpAppointmentsForSources(sourceIdsToFetch);
          sourceIdsToFetch.forEach(sourceId => followUpAppointmentsCache.current.set(sourceId, []));
          fetchedFollowUps.forEach(followUp => {
            const followUps = followUpAppointmentsCache.current.get(followUp.originalAppointmentId) || [];
            followUpAppointmentsCache.current.set(followUp.originalAppointmentId, [...followUps, followUp]);
          });
        }
        const relatedFollowUps = sourceAppointmentIds.flatMap(sourceId =>
          followUpAppointmentsCache.current.get(sourceId) || []
        );
        relatedFollowUps.forEach(appointment => appointmentById.set(appointment.id, appointment));

        const missingParentIds = [...new Set(
          [...appointmentById.values()]
            .filter(appointment =>
              appointment.isFollowUp === true &&
              appointment.originalAppointmentId &&
              !appointmentById.has(appointment.originalAppointmentId)
            )
            .map(appointment => appointment.originalAppointmentId)
            .filter(parentId => !followUpParentCache.current.has(parentId))
        )];
        const missingParentSnapshots = await Promise.all(
          missingParentIds.map(parentId => getDoc(doc(db, 'appointments', parentId)))
        );
        missingParentSnapshots.forEach(parentSnapshot => {
          if (!parentSnapshot.exists()) {
            followUpParentCache.current.set(parentSnapshot.id, null);
            return;
          }
          const parentAppointment = { id: parentSnapshot.id, ...parentSnapshot.data() };
          followUpParentCache.current.set(parentSnapshot.id, parentAppointment);
        });
        missingParentIds.forEach(parentId => {
          const parentAppointment = followUpParentCache.current.get(parentId);
          if (!parentAppointment) return;
          const matchingFollowUp = [...appointmentById.values()].find(appointment =>
            appointment.isFollowUp === true &&
            appointment.originalAppointmentId === parentId
          );
          if (matchingFollowUp?.uid === parentAppointment.uid) {
            appointmentById.set(parentId, parentAppointment);
          }
        });

        const followUpsByParentId = new Map();
        appointmentById.forEach(appointment => {
          if (
            appointment.isFollowUp !== true ||
            !appointment.originalAppointmentId ||
            !appointmentById.has(appointment.originalAppointmentId) ||
            appointmentById.get(appointment.originalAppointmentId).uid !== appointment.uid
          ) return;

          const followUps = followUpsByParentId.get(appointment.originalAppointmentId) || [];
          followUps.push(appointment);
          followUpsByParentId.set(appointment.originalAppointmentId, followUps);
        });

        const historyAppointments = [...appointmentById.values()]
          .filter(appointment => !(
            appointment.isFollowUp === true &&
            appointment.originalAppointmentId &&
            appointmentById.has(appointment.originalAppointmentId) &&
            appointmentById.get(appointment.originalAppointmentId).uid === appointment.uid
          ))
          .map(appointment => ({
            ...appointment,
            followUpAppointments: followUpsByParentId.get(appointment.id) || []
          }));
        const uids = [...new Set(historyAppointments.map(apt => apt.uid).filter(Boolean))];
        const assistanceData = {};

        await Promise.all(uids.map(async uid => {
          try {
            const snapshot = await getDoc(doc(db, 'users', uid));
            const data = snapshot.exists() ? snapshot.data() : {};
            assistanceData[uid] = {
              assistanceRequired: data.assistance?.assistanceRequired || false,
              reasonForAssistance: data.assistance?.reasonForAssistance || []
            };
          } catch (err) {
            console.error('Error fetching assistance data for user:', uid, err);
            assistanceData[uid] = {
              assistanceRequired: false,
              reasonForAssistance: []
            };
          }
        }));

        const enrichedAppointments = historyAppointments.map(apt => ({
          ...apt,
          assistanceRequired: assistanceData[apt.uid]?.assistanceRequired || false,
          reasonForAssistance: assistanceData[apt.uid]?.reasonForAssistance || []
        }));

        if (isActive) setAppointmentsWithAssistance(enrichedAppointments);
      } catch (err) {
        console.error('Error loading follow-up appointment history:', err);
        setBannerMessage(`Follow-up history could not be loaded: ${err.message}`);
        setBannerType('error');
        if (isActive) {
          setAppointmentsWithAssistance(appointments.map(appointment => ({
            ...appointment,
            followUpAppointments: [],
            assistanceRequired: false,
            reasonForAssistance: []
          })));
        }
      }
    };

    fetchHistoryData();
    return () => {
      isActive = false;
    };
  }, [appointments, followUpRefreshVersion]);

  const sortedAppointments = sortAppointments(filteredAppointments, sortConfig, (appointment, key) => {
    switch (key) {
      case "status":
        return appointment.status ? getStatusLabel(appointment.status) : null;
      case "patientName":
        return appointment.patientName || null;
      case "reference":
        return appointment.reference || null;
      case "clinicName":
        return appointment.clinicName || null;
      case "serviceName":
        return getAppointmentTextValue(appointment.serviceName) || null;
      case "dateTime":
        return getAppointmentDateTimeValue(appointment);
      case "blockchainStatus":
        return appointment.blockchain?.status || null;
      default:
        return null;
    }
  });

  const totalPages = Math.ceil(sortedAppointments.length / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;

  const endIndex = startIndex + itemsPerPage;

  const paginatedAppointments = sortedAppointments.slice(startIndex, endIndex);

  const handleSort = (key) => {
    setSortConfig((current) => getNextAppointmentSortConfig(current, key));
    setCurrentPage(1);
  };

  const getSortHeaderProps = (key, label) => ({
    onClick: () => handleSort(key),
    onKeyDown: (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleSort(key);
      }
    },
    tabIndex: 0,
    title: getAppointmentSortTitle(label, key, sortConfig),
    "aria-label": getAppointmentSortTitle(label, key, sortConfig),
    "aria-sort": sortConfig?.key !== key
      ? "none"
      : sortConfig.direction === "asc" ? "ascending" : "descending",
    style: { cursor: "pointer" }
  });



  return (
    <div className="account-management-container">

      <CreateAppointmentModal
        isOpen={Boolean(followUpAppointmentTarget)}
        onClose={() => setFollowUpAppointmentTarget(null)}
        setNotification={setFollowUpNotification}
        followUpAppointment={followUpAppointmentTarget}
        onCreated={() => {
          if (followUpAppointmentTarget?.id) {
            followUpAppointmentsCache.current.delete(followUpAppointmentTarget.id);
          }
          setFollowUpRefreshVersion(version => version + 1);
          Promise.resolve(refreshAppointments?.()).catch(error => {
            console.error('Error refreshing appointments after follow-up creation:', error);
          });
        }}
      />

      <Banner
        message={followUpFeedback.message}
        type={followUpFeedback.type}
        onClose={() => setFollowUpFeedback({ message: "", type: "error" })}
      />

      <ExportModal 
        isOpen={isExportModalOpen} 
        onClose={() => setIsExportModalOpen(false)} 
        onExport={handleExport} 
        isExporting={isExporting} 
      />

      {/* Appointment History Header */}
      <div className="content-library-header-custom" style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Medical Professional - Left Aligned */}
        <div style={{
          position: 'absolute',
          left: '32px',
          top: '50%',
          transform: 'translateY(-50%)'
        }}>
          <div style={{
            padding: '16px 32px',
            borderRadius: '50px',
            background: 'rgba(147, 197, 253, 0.2)',
            border: '1px solid rgba(147, 197, 253, 0.3)'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
              letterSpacing: '0.02em',
              textShadow: 'none'
            }}>
              Medical Professional
            </h2>
          </div>
        </div>

        {/* Title and Subtitle */}
        <div style={{ textAlign: 'right' }}>
          <h2 style={{
            margin: 0,
            fontSize: '36px',
            fontWeight: '800',
            color: '#ffffff !important',
            background: 'none !important',
            WebkitBackgroundClip: 'unset !important',
            WebkitTextFillColor: '#ffffff !important',
            backgroundClip: 'unset !important',
            letterSpacing: '-0.02em',
            textShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            Appointment History
          </h2>
          <p style={{
            margin: '12px 0 0 0',
            fontSize: '18px',
            color: '#ffffff',
            fontWeight: '500',
            textShadow: '0 1px 4px rgba(0,0,0,0.15)'
          }}>
            View all past and completed appointment records
          </p>

        </div>

        {/* CSS Animations */}
        <style>{`
          .content-library-header-custom h2 {
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: #ffffff !important;
            background-clip: unset !important;
          }
          .content-library-header-custom p {
            color: #ffffff !important;
          }
        `}</style>
      </div>



      {/* Top Row: Search, Date From, Date To, Service, Refresh */}
      <div className="admin-aura-top-controls" style={{ marginTop: 20 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          <input
            type="text"
            placeholder="Search History"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="admin-aura-search"
            style={{ paddingLeft: 36, width: '100%' }}
          />
        </div>

        <div style={{ position: 'relative', flex: '0 0 140px' }}>
          <FiCalendar style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          <input
            type="date"
            className="admin-aura-search"
            style={{ paddingLeft: 36, width: '100%' }}
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500', flexShrink: 0 }}>to</div>

        <div style={{ position: 'relative', flex: '0 0 140px' }}>
          <FiCalendar style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          <input
            type="date"
            className="admin-aura-search"
            style={{ paddingLeft: 36, width: '100%' }}
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <select
          className="admin-aura-select"
          value={serviceFilter}
          onChange={(e) => {
            setServiceFilter(e.target.value);
            setCurrentPage(1);
          }}
          style={{ flex: '0 0 180px' }}
        >
          <option value="">All Services</option>
          {SERVICE_FILTER_OPTIONS.map((service) => (
            <option key={service.value} value={service.value}>{service.label}</option>
          ))}
        </select>

        <button 
          onClick={async () => {
            setIsRefreshing(true);
            setCurrentPage(1);
            followUpAppointmentsCache.current.clear();
            followUpParentCache.current.clear();
            setFollowUpRefreshVersion(version => version + 1);
            try {
              if (typeof refreshAppointments === 'function') {
                await refreshAppointments();
              }
            } catch (err) {
              console.error('Error refreshing history appointments:', err);
            } finally {
              setIsRefreshing(false);
            }
          }}
          disabled={isRefreshing}
          className="admin-primary-gradient-btn"
          style={{ opacity: isRefreshing ? 0.6 : 1, cursor: isRefreshing ? 'not-allowed' : 'pointer' }}
        >
          <FiRefreshCw className={isRefreshing ? "spinning" : ""} />
          {isRefreshing ? "Refreshing..." : "Refresh"}
        </button>
        <button
          onClick={() => setIsExportModalOpen(true)}
          className="admin-primary-gradient-btn"
        >
          <FiFileText /> Export Report
        </button>
      </div>



      {/* Custom Tooltip for Action Buttons */}
      {tooltip.visible && (
        <div
          className={`admin-action-tooltip ${tooltip.type}`}
          style={{
            left: tooltip.x,
            top: tooltip.y
          }}
        >
          {tooltip.text}
        </div>
      )}



      {/* Second Row: Status Buttons + Export to PDF + Pagination */}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>

        <div className="floating-index-tabs ma-status-tabs" aria-label="Filter appointments by status" style={{ display: "flex", gap: "8px" }}>

          {STATUS_FILTER_TABS.map((tab) => (

            <button

              key={tab.value || "all"}

              type="button"

              className={`floating-tab ${statusFilter === tab.value ? "active floating-tab-active" : ""}`}

              onClick={() => {

                setStatusFilter(tab.value);

                setCurrentPage(1);

              }}

            >

              {tab.label}

              <span className="tab-count">{getStatusTabCount(tab.value)}</span>

            </button>

          ))}

        </div>



        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>

            <div style={{ fontSize: "14px", color: "#6b7280" }}>

              {filteredAppointments.length > 0 ? (

                `${startIndex + 1}-${Math.min(endIndex, filteredAppointments.length)} of ${filteredAppointments.length}`

              ) : (

                '0 of 0'

              )}

            </div>



            {/* Pagination Arrows */}

            <div style={{ display: "flex", gap: "8px" }}>

              <button 

                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}

                disabled={currentPage === 1}

                style={{ 

                  backgroundColor: '#1e3a8a', 

                  color: 'white', 

                  border: 'none', 

                  padding: '8px 12px', 

                  borderRadius: '4px', 

                  cursor: 'pointer',

                  width: '32px'

                }}

              >

                ←

              </button>

              

              <button 

                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}

                disabled={currentPage === totalPages || totalPages === 0}

                style={{ 

                  backgroundColor: '#1e3a8a', 

                  color: 'white', 

                  border: 'none', 

                  padding: '8px 12px', 

                  borderRadius: '4px', 

                  cursor: 'pointer',

                  width: '32px'

                }}

              >

                →

              </button>

            </div>

        </div>

      </div>



      <div className="ma-table-wrap">

        <table className="ma-table">

          <thead>

            <tr style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}>

              <th {...getSortHeaderProps("status", "Appointment Status")} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none', cursor: 'pointer' }}>STATUS {getAppointmentSortIndicator(sortConfig, "status")}</th>

              <th {...getSortHeaderProps("patientName", "Patient Name")} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none', cursor: 'pointer' }}>PATIENT {getAppointmentSortIndicator(sortConfig, "patientName")}</th>

              <th {...getSortHeaderProps("reference", "Reference Number")} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none', cursor: 'pointer' }}>REFERENCE # {getAppointmentSortIndicator(sortConfig, "reference")}</th>

              <th {...getSortHeaderProps("clinicName", "Clinic")} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none', cursor: 'pointer' }}>CLINIC {getAppointmentSortIndicator(sortConfig, "clinicName")}</th>

              <th {...getSortHeaderProps("serviceName", "Service")} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none', cursor: 'pointer' }}>SERVICE {getAppointmentSortIndicator(sortConfig, "serviceName")}</th>

              <th {...getSortHeaderProps("dateTime", "Appointment Date & Time")} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none', cursor: 'pointer' }}>DATE & TIME {getAppointmentSortIndicator(sortConfig, "dateTime")}</th>

              <th {...getSortHeaderProps("blockchainStatus", "Blockchain Status")} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none', cursor: 'pointer' }}>BLOCKCHAIN {getAppointmentSortIndicator(sortConfig, "blockchainStatus")}</th>

              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>ACTIONS</th>

            </tr>

          </thead>

          <tbody>

            {loading ? (
              <>
                {[...Array(10)].map((_, i) => (
                  <tr key={i} className="admin-aura-skeleton-row">
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '40%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${60 + (i % 3) * 15}%` }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '50%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${55 + (i % 4) * 10}%` }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '65%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '55%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '45%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-actions" /></td>
                  </tr>
                ))}
              </>
            ) : paginatedAppointments.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
                  No appointments found
                </td>
              </tr>
            ) : (
              paginatedAppointments.map((apt) => (

              <tr key={apt.id}>

                <td>

                  <span className={`ma-badge ${getStatusBadgeClass(apt.status)}`}>

                    {getStatusLabel(apt.status)}

                  </span>

                </td>

                <td>
                  <div>{apt.patientName}</div>
                  {apt.reasonForAssistance?.length > 0 && (
                    <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {apt.reasonForAssistance.map((reason, idx) => (
                        <span
                          key={idx}
                          style={{
                            fontSize: '10px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontWeight: '500',
                            textTransform: 'capitalize',
                            backgroundColor: getAssistanceBadgeColor(reason),
                            color: getAssistanceBadgeTextColor(reason)
                          }}
                        >
                          {reason}
                        </span>
                      ))}
                    </div>
                  )}
                </td>

                <td>
                  <div>{apt.reference}</div>
                  {apt.followUpAppointments?.some(f => f.status === 'COMPLETED') && (
                    <span style={{
                      display: 'inline-block',
                      marginTop: '4px',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      background: '#dbeafe',
                      color: '#1e40af',
                      fontSize: '10px',
                      fontWeight: '600'
                    }}>
                      Has Follow-up
                    </span>
                  )}
                </td>

                <td>{apt.clinicName}</td>

                <td>{apt.serviceName}</td>

                <td>

                  <div>{apt.date}</div>

                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{apt.time}</div>

                </td>

                <td>
                  {apt.blockchain?.status ? (
                    <span 
                      style={{
                        fontSize: '11px',
                        padding: '4px 8px',
                        borderRadius: '999px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        cursor: apt.blockchain.status === 'verified' ? 'pointer' : 'default',
                        ...(apt.blockchain.status === 'verified' ? {
                          background: '#ecfdf5',
                          color: '#059669',
                          border: '1px solid #10b981'
                        } : apt.blockchain.status === 'pending' ? {
                          background: '#fef3c7',
                          color: '#d97706',
                          border: '1px solid #f59e0b'
                        } : apt.blockchain.status === 'read_only' ? {
                          background: '#f3f4f6',
                          color: '#6b7280',
                          border: '1px solid #9ca3af'
                        } : {
                          background: '#fef2f2',
                          color: '#dc2626',
                          border: '1px solid #ef4444'
                        })
                      }}
                      onClick={() => {
                        if (apt.blockchain.status === 'verified') {
                          setViewing(apt);
                          setShowBlockchainModal(true);
                        }
                      }}
                      title={apt.blockchain.status === 'verified' ? 'Click to view blockchain details' : ''}
                    >
                      {apt.blockchain.status === 'verified' && <FiShield size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />}
                      {apt.blockchain.status}
                    </span>
                  ) : (
                    <span style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>None</span>
                  )}
                </td>

                <td>

                  <div className="ma-row-actions">

                    {apt.status === 'COMPLETED' && (

                      <>
                        <button
                          type="button"
                          className="ma-action-btn complete"
                          aria-label="Add Follow-Up Appointment"
                          title="Add Follow-Up Appointment"
                          onClick={() => setFollowUpAppointmentTarget(apt)}
                          style={{ background: '#eff6ff', color: '#1d4ed8', borderColor: '#3b82f6' }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#dbeafe';
                            handleActionMouseEnter(e, "Add Follow-Up Appointment", 'approve');
                          }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#eff6ff';
                            handleActionMouseLeave();
                          }}
                        >
                          <FiPlus size={16} />
                        </button>

                        <button

                          type="button"

                          className="ma-action-btn notes"

                          onClick={() => {

                            setViewing(apt);

                            setShowAttachmentsModal(true);

                          }}

                          style={{ background: '#fef3c7', color: '#d97706', borderColor: '#f59e0b' }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#fde68a';
                            handleActionMouseEnter(e, "View Attachments", 'reschedule');
                          }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#fef3c7';
                            handleActionMouseLeave();
                          }}

                        >

                          <FiPaperclip size={16} />

                        </button>

                      </>

                    )}

                    <button

                      type="button"

                      className="ma-action-btn download"

                      onClick={() => handleDownload(apt)}

                      style={{

                        display: 'flex',

                        alignItems: 'center',

                        justifyContent: 'center',

                        padding: '8px',

                        background: '#f3f4f6',

                        border: '1px solid #d1d5db',

                        borderRadius: '6px',

                        cursor: 'pointer',

                        color: '#6b7280',

                        transition: 'all 0.2s'

                      }}
                      onMouseEnter={(e) => { e.target.style.background = '#e5e7eb'; handleActionMouseEnter(e, "Download Slip", 'gray'); }}
                      onMouseMove={handleActionMouseMove}
                      onMouseLeave={(e) => { e.target.style.background = '#f3f4f6'; handleActionMouseLeave(); }}
                    >

                      <FiDownload size={16} />

                    </button>

                  </div>

                </td>

              </tr>

              ))
            )}

          </tbody>

        </table>

      </div>


      {/* BLOCKCHAIN MODAL */}
      {viewing && showBlockchainModal && createPortal(
        <div className="ma-modal-backdrop">
          <div className="ma-modal-card" style={{ maxWidth: '600px' }}>
            <button
              className="ma-modal-close"
              type="button"
              onClick={() => {
                setShowBlockchainModal(false);
                setViewing(null);
                setBannerMessage("");
              }}
            >
              <FiX />
            </button>
            
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiShield size={20} color="#2563eb" />
              Blockchain Verification
            </h3>

            <Banner 
              message={bannerMessage} 
              type={bannerType} 
              onClose={() => setBannerMessage("")} 
            />

            <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, paddingRight: '4px' }}>
              {/* Tamper Warning */}
              {viewing.blockchain.status === 'verified' && (
                <div style={{ marginBottom: '16px', padding: '12px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FiAlertCircle size={16} color="#dc2626" />
                  <span style={{ fontSize: '13px', color: '#991b1b', fontWeight: '500' }}>
                    This record is blockchain-verified. Any modifications will be detected as tampering.
                  </span>
                </div>
              )}

              <div className="ma-modal-details" style={{ background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <div className="ma-detail-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Status</div>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>
                    <span 
                      style={{
                        fontSize: '12px',
                        padding: '4px 8px',
                        borderRadius: '999px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        ...(viewing.blockchain.status === 'verified' ? {
                          background: '#ecfdf5',
                          color: '#059669',
                          border: '1px solid #10b981'
                        } : viewing.blockchain.status === 'pending' ? {
                          background: '#fef3c7',
                          color: '#d97706',
                          border: '1px solid #f59e0b'
                        } : {
                          background: '#f3f4f6',
                          color: '#6b7280',
                          border: '1px solid #9ca3af'
                        })
                      }}
                    >
                      {viewing.blockchain.status === 'verified' && <FiShield size={10} style={{ marginRight: '4px', verticalAlign: 'middle' }} />}
                      {viewing.blockchain.status}
                    </span>
                  </div>
                </div>
                {viewing.blockchain.network && (
                  <div className="ma-detail-row" style={{ display: 'block', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>Network</div>
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>{viewing.blockchain.network}</div>
                  </div>
                )}
                {viewing.blockchain.verifiedAt && (
                  <div className="ma-detail-row" style={{ display: 'block', padding: '8px 0' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>Verified At</div>
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>{new Date(viewing.blockchain.verifiedAt).toLocaleString()}</div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>,
        document.body
      )}



      {/* ATTACHMENTS MODAL */}

      {viewing && showAttachmentsModal && createPortal(

        <div className="ma-modal-backdrop">

          <div className="ma-modal-card wide" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

            <button

              className="ma-modal-close"

              type="button"

              onClick={() => {

                setShowAttachmentsModal(false);

                setViewing(null);

                setClinicalNote("");

                setAttachmentFiles([]);
                setRemovedAttachmentIds([]);

                setExistingNotes([]);
                setSelectedNoteId("");
                setShowAttachmentEditor(false);
                setNoteError("");

                setBannerMessage("");

              }}

            >

              <FiX />

            </button>

            <h3>Appointment Attachments & Findings</h3>

            {(() => {
              const attachmentExpiry = getAttachmentExpiry(viewing);
              const isExpired = attachmentExpiry ? attachmentExpiry <= new Date() : false;
              return (
                <div style={{
                  margin: '0 0 20px',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: `1px solid ${isExpired ? '#fecaca' : '#bfdbfe'}`,
                  background: isExpired ? '#fef2f2' : '#eff6ff',
                  color: isExpired ? '#991b1b' : '#1e40af',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '13px'
                }}>
                  <FiClock size={18} />
                  <span>
                    <strong>Attachment availability:</strong>{' '}
                    {attachmentExpiry
                      ? `${isExpired ? 'Expired on' : 'Available until'} ${attachmentExpiry.toLocaleString()}`
                      : 'Expiry date unavailable'}
                  </span>
                </div>
              );
            })()}

            

            <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, paddingRight: '4px' }}>

            <Banner 

              message={bannerMessage} 

              type={bannerType} 

              onClose={() => setBannerMessage("")} 

            />



            <div className="ma-modal-details" style={{ marginBottom: '24px' }}>

              <div className="ma-detail-row">

                <span className="ma-detail-label">Patient</span>

                <span className="ma-detail-value">{viewing?.patientName}</span>

              </div>

              <div className="ma-detail-row">

                <span className="ma-detail-label">Reference #</span>

                <span className="ma-detail-value">{viewing?.reference}</span>

              </div>

            </div>

            <div style={{ marginTop: '32px', paddingTop: '20px', borderTop: '1px solid #f3f4f6' }}>

              <div style={{ display: 'flex', gap: '24px' }}>

                {/* Left: Existing Notes */}

                <div style={{ flex: '0 0 50%', minWidth: 0 }}>

                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>Consultation Findings</h4>

                  {existingNotes.length > 0 ? (

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '250px', overflowY: 'auto', paddingRight: '8px' }}>

                      {existingNotes.map((note, idx) => (

                        <div key={idx} style={{ padding: '12px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#f9fafb' }}>
                          <p style={{ margin: 0, fontSize: '13px', color: '#374151', whiteSpace: 'pre-wrap' }}>{note.text}</p>

                          <div style={{ marginTop: '6px', fontSize: '10px', color: '#9ca3af', display: 'flex', justifyContent: 'space-between' }}>

                            <span>By {note.createdBy}</span>

                            <span>{new Date(note.createdAt).toLocaleDateString()}</span>

                          </div>

                        </div>

                      ))}

                    </div>

                  ) : (

                    <p style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>No findings recorded yet.</p>

                  )}

                </div>



                {/* Right: Existing Attachments */}

                <div style={{ flex: '0 0 calc(50% - 24px)', minWidth: 0 }}>

                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>Uploaded Documents</h4>

                  {viewing?.attachments?.length > 0 ? (

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '250px', overflowY: 'auto', paddingRight: '8px' }}>

                      {viewing.attachments.map((file, index) => (

                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff' }}>

                          <FiPaperclip color="#6b7280" />

                          <div style={{ flex: 1, overflow: 'hidden' }}>

                            <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#2563eb', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>

                              {file.fileName || 'Unnamed File'}

                            </a>

                            <span style={{ fontSize: '10px', color: '#9ca3af' }}>{new Date(file.uploadedAt).toLocaleDateString()} by {file.uploadedBy}</span>

                          </div>

                        </div>

                      ))}

                    </div>

                  ) : (

                    <p style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>No files found.</p>

                  )}

                </div>

              </div>

            </div>

            {/* Follow-up Appointments — COMPLETED only, shown after findings */}
            {(() => {
              const completedFollowUps = (viewing?.followUpAppointments || []).filter(f => f.status === 'COMPLETED');
              if (completedFollowUps.length === 0) return null;
              return (
                <section style={{
                  marginTop: '28px',
                  paddingTop: '20px',
                  borderTop: '1px solid #f3f4f6'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '9px',
                      background: '#dbeafe',
                      color: '#2563eb'
                    }}>
                      <FiCalendar size={16} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: 0, color: '#1e3a8a', fontSize: '14px' }}>Follow-up Appointments</h4>
                      <span style={{ color: '#64748b', fontSize: '11px' }}>
                        {completedFollowUps.length} completed {completedFollowUps.length === 1 ? 'follow-up' : 'follow-ups'}
                      </span>
                    </div>
                  </div>
                  {completedFollowUps.map(followUp => {
                    const followUpNotes = followUp.attachmentNotes || [];
                    const followUpFiles = followUp.attachments || [];
                    return (
                      <article key={followUp.id} style={{
                        marginTop: '12px',
                        border: '1px solid #dbeafe',
                        borderLeft: '4px solid #3b82f6',
                        borderRadius: '10px',
                        background: '#f8fbff',
                        overflow: 'hidden'
                      }}>
                        {/* Follow-up header */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: '8px',
                          padding: '12px 14px',
                          borderBottom: (followUpNotes.length > 0 || followUpFiles.length > 0) ? '1px solid #dbeafe' : 'none'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: '28px',
                              height: '28px',
                              flex: '0 0 28px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '7px',
                              background: '#eff6ff',
                              color: '#3b82f6'
                            }}>
                              <FiClock size={14} />
                            </div>
                            <div>
                              <strong style={{ color: '#1e293b', fontSize: '13px' }}>
                                {followUp.serviceName || 'Follow-up Appointment'}
                              </strong>
                              <div style={{ marginTop: '3px', color: '#475569', fontSize: '12px' }}>
                                {followUp.date || 'Date unavailable'} <span style={{ color: '#94a3b8' }}>•</span> {followUp.time || 'Time unavailable'}
                              </div>
                              <div style={{ marginTop: '2px', color: '#64748b', fontSize: '11px' }}>
                                Reference # <span style={{ color: '#334155', fontWeight: 600 }}>{followUp.reference || 'N/A'}</span>
                              </div>
                            </div>
                          </div>
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: '999px',
                            background: '#dcfce7',
                            color: '#15803d',
                            fontSize: '10px',
                            fontWeight: 700,
                            alignSelf: 'flex-start'
                          }}>
                            Completed
                          </span>
                        </div>

                        {/* Follow-up findings & attachments */}
                        {(followUpNotes.length > 0 || followUpFiles.length > 0) && (
                          <div style={{ display: 'flex', gap: '20px', padding: '14px', flexWrap: 'wrap' }}>
                            {/* Findings */}
                            <div style={{ flex: '1 1 45%', minWidth: '200px' }}>
                              <h5 style={{ fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <FiFileText size={13} /> Consultation Findings
                              </h5>
                              {followUpNotes.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {followUpNotes.map((note, idx) => (
                                    <div key={note.id || idx} style={{ padding: '10px', borderRadius: '7px', border: '1px solid #e5e7eb', background: '#ffffff' }}>
                                      <p style={{ margin: 0, fontSize: '12px', color: '#374151', whiteSpace: 'pre-wrap' }}>{note.text}</p>
                                      <div style={{ marginTop: '5px', fontSize: '10px', color: '#9ca3af', display: 'flex', justifyContent: 'space-between' }}>
                                        <span>By {note.createdBy || 'Medical Professional'}</span>
                                        {note.createdAt && <span>{new Date(note.createdAt).toLocaleDateString()}</span>}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>No findings recorded.</p>
                              )}
                            </div>
                            {/* Uploaded documents */}
                            <div style={{ flex: '1 1 45%', minWidth: '200px' }}>
                              <h5 style={{ fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <FiPaperclip size={13} /> Uploaded Documents
                              </h5>
                              {followUpFiles.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                                  {followUpFiles.map((file, idx) => (
                                    <div key={file.fileUrl || idx} style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '9px', borderRadius: '7px', border: '1px solid #e5e7eb', background: '#ffffff' }}>
                                      <FiPaperclip size={14} color="#6b7280" />
                                      <div style={{ flex: 1, overflow: 'hidden' }}>
                                        <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#2563eb', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {file.fileName || 'Unnamed File'}
                                        </a>
                                        <span style={{ fontSize: '10px', color: '#9ca3af' }}>{file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString() : ''}{file.uploadedBy ? ` by ${file.uploadedBy}` : ''}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>No files uploaded.</p>
                              )}
                            </div>
                          </div>
                        )}
                        {followUpNotes.length === 0 && followUpFiles.length === 0 && (
                          <p style={{ margin: 0, padding: '12px 14px', fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>
                            No findings or attachments for this follow-up.
                          </p>
                        )}
                      </article>
                    );
                  })}
                </section>
              );
            })()}

            </div>



            <div className="ma-modal-footer" style={{ marginTop: '32px', flexShrink: 0 }}>

              <button 

                className="ma-modal-btn secondary" 

                onClick={() => {

                  setShowAttachmentsModal(false);

                  setViewing(null);

                }}

              >

                Close

              </button>

            </div>

          </div>

        </div>,

        document.body

      )}

    </div>

  );

};



export default AppointmentHistory;